// Utility to derive the current default configurator state
// from global settings, mirroring the logic used on initial load.

export function deriveDefaultsFromGlobals(globalSettings) {
  const gs = globalSettings || { placeholders: {}, defaults: {} };
  const defaultFooterEventName = 'EVENT 2026';
  const configuredFooterEventName =
    typeof gs.placeholders?.footerEventName === 'string' &&
    gs.placeholders.footerEventName.trim()
      ? gs.placeholders.footerEventName.trim()
      : defaultFooterEventName;

  const defaultTemplate = {
    id: 'none',
    name: 'None',
    category: '',
    includeWalletButtons: false,
    includeOuterBorder: false,
    includeCoName: false,
    includeAssociation: false,
    includeAddress: false,
    showEmail: false,
    showPhone: false,
    showWebsite: false,
    showSocialMedia: false,
    eventAbbreviation: 'EVENT',
    copyrightYear: '2026',
    bodyBackgroundColor: '#ffffff',
    bodyTextColor: '#333333',
    linkColor: '#00519e',
    dividerColor: '#dddddd',
    footerBackgroundColor: '#f5f5f5',
    footerTextColor: '#555555',
    templateFontFamily: 'Roboto',
    bodyFontSize: 14,
    paragraphLineHeight: 1.6,
    tableBorderRadius: 10,
    salutationInput: 'Dear ${p.firstName} ${p.lastName},',
    salutationFontSize: 16,
    salutationStyles: ['normal'],
    closingText: 'Sincerely,',
    salutationTranslations: {
      en: 'Dear ${p.firstName} ${p.lastName},',
      de: 'Guten Tag ${p.firstName} ${p.lastName},',
      fr: 'Chère/Cher ${p.firstName} ${p.lastName},',
      ar: '&{anrede}،',
    },
    closingTextTranslations: {
      en: 'Sincerely,',
      de: 'Mit freundlichen Grüßen',
      fr: 'Cordialement,',
      ar: 'مع أطيب التحيات،',
    },
    organizingTeamName: 'The Event Team',
    teamNameFontSize: 14,
    teamNameStyles: ['bold'],
    footerFontSize: 12,
    footerStyles: ['normal'],
    footerPadding: 20,
    footerBorderRadius: 10,
    lineWidth: 1,
    outerBorderColor: '#e5e7eb',
    banners: [],
    includeTopPadding: false,
    topPaddingHeight: 20,
    topPaddingBackgroundColor: '#f5f5f5',
    topPaddingTextColor: '#555555',
    topPaddingText: '',
    includeFooter: true,
    footerEventName: configuredFooterEventName,
    footerText: `© ${configuredFooterEventName} - All rights reserved.`,
    footerTextTranslations: {
      en: `© ${configuredFooterEventName} - All rights reserved.`,
      de: `© ${configuredFooterEventName} – Alle Rechte vorbehalten.`,
      fr: `© ${configuredFooterEventName} – Tous droits réservés.`,
      ar: `© ${configuredFooterEventName} – جميع الحقوق محفوظة.`,
    },
    includeConfidentialityNote: false,
    confidentialityNoteFontSize: 10,
    confidentialityNoteStyles: ['normal'],
    confidentialityNote:
      'Confidentiality Note: This message is intended only for the use of the named recipient(s) and may contain confidential and/or privileged information. If you are not the intended recipient, please contact the sender and delete the message. Any unauthorized use of the information contained in this message is prohibited.',
    confidentialityNoteTranslations: {
      en: 'Confidentiality Note: This message is intended only for the use of the named recipient(s) and may contain confidential and/or privileged information. If you are not the intended recipient, please contact the sender and delete the message. Any unauthorized use of the information contained in this message is prohibited.',
      de: 'Vertraulichkeitshinweis: Diese Nachricht ist nur für die benannte(n) empfangende(n) Person(en) bestimmt und kann vertrauliche und/oder privilegierte Informationen enthalten. Falls Sie nicht die vorgesehene empfangende Person sind, kontaktieren Sie bitte die absendende Person und löschen Sie die Nachricht. Jede unbefugte Nutzung der in dieser Nachricht enthaltenen Informationen ist untersagt.',
      fr: 'Note de confidentialité: Ce message est destiné uniquement à l’usage du ou des destinataires désignés et peut contenir des informations confidentielles et/ou privilégiées. Si vous n’êtes pas le destinataire prévu, veuillez contacter l’expéditeur et supprimer le message. Toute utilisation non autorisée des informations contenues dans ce message est interdite.',
      ar: 'ملاحظة السرية: هذه الرسالة مخصصة فقط لاستخدام المستلم(ين) المحدد(ين) وقد تحتوي على معلومات سرية و/أو مميزة. إذا لم تكن المستلم المقصود، يرجى التواصل مع المرسل وحذف الرسالة. أي استخدام غير مصرح به للمعلومات الواردة في هذه الرسالة محظور.',
    },
    contactEmail: 'info@example.com',
    contactPhone: '+1 234 567 890',
    websiteUrl: 'www.example.com',
    websiteDisplayText: 'www.example.com',
    contactOrder: ['email', 'phone', 'website'],
    socialTextInput: 'Follow us on social media',
    socialFooterText: '',
    socialMediaBackgroundColor: '#f8f9fa',
    socialHeaderStyles: ['normal'],
    socialFooterStyles: ['normal'],
    socialLinks: {
      facebookUrl: '',
      twitterUrl: '',
      linkedinUrl: '',
      instagramUrl: '',
      youtubeUrl: '',
      miscUrl: '',
    },
    socialIconSize: 16,
    socialIconBorderRadius: 0,
    showCongressAppLinks: false,
    congressAppHeadline: 'Get the congress app',
    congressAppBackgroundColor: '#f8f9fa',
    congressAppLinks: {
      appleUrl: '',
      googleUrl: '',
    },
    sectionTitles: [],
    ctaButtons: [],
    ctaStyleSlots: [],
    textTemplateContent: '',
    textTemplateLink: null,
    textTemplateLanguage: '',
    calendarProviders: {
      apple: true,
      google: true,
      outlook: false,
      yahoo: false,
    },
    calendarProviderLogos: {},
    calendarProviderLabels: {},
    calendarProviderLogoHeight: 32,
    calendarProviderButtonShape: 'circle',
    includeCalendarButton: false,
    calendarButtonText: 'Add to calendar',
    calendarButtonBackgroundColor: '#0f172a',
    calendarButtonTextColor: '#ffffff',
    calendarButtonFontSize: 14,
    calendarButtonPaddingX: 20,
    calendarButtonPaddingY: 10,
    calendarButtonBorderRadius: 6,
    calendarButtonFileName: 'event.ics',
    calendarEventTitle: '',
    calendarEventLocation: '',
    calendarEventDescription: '',
    calendarEventStart: '',
    calendarEventEnd: '',
    calendarEventUrl: '',
    calendarEventUid: '',
    walletBackgroundColor: '#f8f9fa',
    walletTextColor: '#333333',
    walletTextAlign: 'left',
    walletIntroText: 'Add to your digital wallet',
    walletIntroFontSize: 14,
    walletIntroStyles: ['normal'],
    walletInstructionsText: 'Tap to add to your wallet',
    walletInstructionsFontSize: 12,
    walletInstructionsStyles: ['normal'],
    // Regmail specific variables
    regmailEnabled: false,
    eventName: '',
    hostingOrganisation: '',
    eventLocation: '',
    eventStartDate: '',
    eventEndDate: '',
    eventTime: '',
    paymentDueDate: '2030-01-01',
    eventEmail: '',
  };

  // Merge placeholders and defaults from globals
  const mergeTranslationMap = (source, fallback) => ({
    ...(fallback || {}),
    ...(source && typeof source === 'object' ? source : {}),
  });

  const salutationTranslations = mergeTranslationMap(
    gs.placeholders?.salutationTranslations,
    defaultTemplate.salutationTranslations
  );
  const closingTextTranslations = mergeTranslationMap(
    gs.placeholders?.closingTextTranslations,
    defaultTemplate.closingTextTranslations
  );
  const footerTextTranslations = mergeTranslationMap(
    gs.placeholders?.footerTextTranslations,
    defaultTemplate.footerTextTranslations
  );
  const confidentialityNoteTranslations = mergeTranslationMap(
    gs.placeholders?.confidentialityNoteTranslations,
    defaultTemplate.confidentialityNoteTranslations
  );
  const configuredFooterText = gs.placeholders?.footerText;
  const configuredConfidentialityNote = gs.placeholders?.confidentialityNote;
  const englishFooterText =
    typeof footerTextTranslations.en === 'string' &&
    footerTextTranslations.en.trim()
      ? footerTextTranslations.en
      : defaultTemplate.footerTextTranslations.en;
  const englishConfidentialityNote =
    typeof confidentialityNoteTranslations.en === 'string' &&
    confidentialityNoteTranslations.en.trim()
      ? confidentialityNoteTranslations.en
      : defaultTemplate.confidentialityNoteTranslations.en;
  const footerText =
    typeof configuredFooterText === 'string' &&
    configuredFooterText !== defaultTemplate.footerText &&
    configuredFooterText.trim()
      ? configuredFooterText
      : englishFooterText || defaultTemplate.footerText;
  const confidentialityNote =
    typeof configuredConfidentialityNote === 'string' &&
    configuredConfidentialityNote !== defaultTemplate.confidentialityNote &&
    configuredConfidentialityNote.trim()
      ? configuredConfidentialityNote
      : englishConfidentialityNote || defaultTemplate.confidentialityNote;

  const merged = {
    ...defaultTemplate,
    salutationInput:
      gs.placeholders?.salutation ?? defaultTemplate.salutationInput,
    eventAbbreviation:
      gs.placeholders?.eventAbbreviation ?? defaultTemplate.eventAbbreviation,
    organizingTeamName:
      gs.placeholders?.teamName ?? defaultTemplate.organizingTeamName,
    bannerImageAltText:
      gs.placeholders?.bannerImageAltText ?? defaultTemplate.bannerImageAltText,
    socialTextInput:
      gs.placeholders?.socialTextInput ?? defaultTemplate.socialTextInput,
    socialFooterText:
      gs.placeholders?.socialFooterText ?? defaultTemplate.socialFooterText,
    contactEmail: gs.placeholders?.contactEmail ?? defaultTemplate.contactEmail,
    contactPhone: gs.placeholders?.contactPhone ?? defaultTemplate.contactPhone,
    websiteUrl: gs.placeholders?.websiteUrl ?? defaultTemplate.websiteUrl,
    websiteDisplayText:
      gs.placeholders?.websiteDisplayText ?? defaultTemplate.websiteDisplayText,
    contactOrder:
      gs.placeholders?.contactOrder ?? defaultTemplate.contactOrder,
    copyrightYear:
      gs.placeholders?.copyrightYear ?? defaultTemplate.copyrightYear,
    bodyBackgroundColor:
      gs.placeholders?.bodyBackgroundColor ??
      defaultTemplate.bodyBackgroundColor,
    bodyTextColor:
      gs.placeholders?.bodyTextColor ?? defaultTemplate.bodyTextColor,
    linkColor: gs.placeholders?.linkColor ?? defaultTemplate.linkColor,
    dividerColor: gs.placeholders?.dividerColor ?? defaultTemplate.dividerColor,
    footerBackgroundColor:
      gs.placeholders?.footerBackgroundColor ??
      defaultTemplate.footerBackgroundColor,
    footerTextColor:
      gs.placeholders?.footerTextColor ?? defaultTemplate.footerTextColor,
    templateFontFamily: (
      gs.placeholders?.templateFontFamily ?? defaultTemplate.templateFontFamily
    ).split(':')[0],
    bodyFontSize: gs.placeholders?.bodyFontSize ?? defaultTemplate.bodyFontSize,
    paragraphLineHeight:
      gs.placeholders?.paragraphLineHeight ??
      defaultTemplate.paragraphLineHeight,
    tableBorderRadius:
      gs.placeholders?.tableBorderRadius ?? defaultTemplate.tableBorderRadius,
    salutationFontSize:
      gs.placeholders?.salutationFontSize ?? defaultTemplate.salutationFontSize,
    salutationStyles:
      gs.placeholders?.salutationStyles ?? defaultTemplate.salutationStyles,
    closingText:
      gs.placeholders?.closingText ?? defaultTemplate.closingText,
    salutationTranslations,
    closingTextTranslations,
    teamNameFontSize:
      gs.placeholders?.teamNameFontSize ?? defaultTemplate.teamNameFontSize,
    teamNameStyles:
      gs.placeholders?.teamNameStyles ?? defaultTemplate.teamNameStyles,
    footerFontSize:
      gs.placeholders?.footerFontSize ?? defaultTemplate.footerFontSize,
    footerStyles: gs.placeholders?.footerStyles ?? defaultTemplate.footerStyles,
    footerPadding:
      gs.placeholders?.footerPadding ?? defaultTemplate.footerPadding,
    footerBorderRadius:
      gs.placeholders?.footerBorderRadius ?? defaultTemplate.footerBorderRadius,
    // Prefer placeholders for admin-edited numeric/style defaults, fallback to defaults
    lineWidth:
      gs.placeholders?.lineWidth ??
      gs.defaults?.lineWidth ??
      defaultTemplate.lineWidth,
    outerBorderColor:
      gs.placeholders?.outerBorderColor ??
      gs.defaults?.outerBorderColor ??
      defaultTemplate.outerBorderColor,
    // Layout & header/footer area
    banners: gs.placeholders?.banners ?? defaultTemplate.banners,
    includeTopPadding:
      gs.placeholders?.includeTopPadding ?? defaultTemplate.includeTopPadding,
    topPaddingHeight:
      gs.placeholders?.topPaddingHeight ?? defaultTemplate.topPaddingHeight,
    topPaddingBackgroundColor:
      gs.placeholders?.topPaddingBackgroundColor ??
      defaultTemplate.topPaddingBackgroundColor,
    topPaddingTextColor:
      gs.placeholders?.topPaddingTextColor ??
      defaultTemplate.topPaddingTextColor,
    topPaddingText:
      gs.placeholders?.topPaddingText ?? defaultTemplate.topPaddingText,
    includeFooter:
      gs.placeholders?.includeFooter ?? defaultTemplate.includeFooter,
    footerEventName:
      gs.placeholders?.footerEventName ?? defaultTemplate.footerEventName,
    footerText,
    footerTextTranslations,
    includeConfidentialityNote:
      gs.placeholders?.includeConfidentialityNote ??
      defaultTemplate.includeConfidentialityNote,
    confidentialityNoteFontSize:
      gs.placeholders?.confidentialityNoteFontSize ??
      defaultTemplate.confidentialityNoteFontSize,
    confidentialityNoteStyles:
      gs.placeholders?.confidentialityNoteStyles ??
      defaultTemplate.confidentialityNoteStyles,
    confidentialityNote,
    confidentialityNoteTranslations,
    socialMediaBackgroundColor:
      gs.placeholders?.socialMediaBackgroundColor ??
      defaultTemplate.socialMediaBackgroundColor,
    socialHeaderStyles:
      gs.placeholders?.socialHeaderStyles ??
      defaultTemplate.socialHeaderStyles,
    socialFooterStyles:
      gs.placeholders?.socialFooterStyles ??
      defaultTemplate.socialFooterStyles,
    socialIconSize:
      gs.placeholders?.socialIconSize ?? defaultTemplate.socialIconSize,
    socialIconBorderRadius:
      gs.placeholders?.socialIconBorderRadius ??
      defaultTemplate.socialIconBorderRadius,
    showCongressAppLinks:
      gs.placeholders?.showCongressAppLinks ??
      defaultTemplate.showCongressAppLinks,
    congressAppHeadline:
      gs.placeholders?.congressAppHeadline ??
      defaultTemplate.congressAppHeadline,
    congressAppBackgroundColor:
      gs.placeholders?.congressAppBackgroundColor ??
      defaultTemplate.congressAppBackgroundColor,
    congressAppLinks: {
      ...defaultTemplate.congressAppLinks,
      ...(gs.placeholders?.congressAppLinks || {}),
    },
    showSocialMedia:
      gs.placeholders?.showSocialMedia ?? defaultTemplate.showSocialMedia,
    // Signature + contact toggles
    includeCoName:
      gs.placeholders?.includeCoName ?? defaultTemplate.includeCoName,
    includeAssociation:
      gs.placeholders?.includeAssociation ?? defaultTemplate.includeAssociation,
    includeAddress:
      gs.placeholders?.includeAddress ?? defaultTemplate.includeAddress,
    showEmail: gs.placeholders?.showEmail ?? defaultTemplate.showEmail,
    showPhone: gs.placeholders?.showPhone ?? defaultTemplate.showPhone,
    showWebsite: gs.placeholders?.showWebsite ?? defaultTemplate.showWebsite,
    walletBackgroundColor:
      gs.placeholders?.walletBackgroundColor ??
      defaultTemplate.walletBackgroundColor,
    walletTextColor:
      gs.placeholders?.walletTextColor ??
      gs.placeholders?.bodyTextColor ??
      defaultTemplate.walletTextColor ??
      defaultTemplate.bodyTextColor,
    walletTextAlign:
      gs.placeholders?.walletTextAlign ?? defaultTemplate.walletTextAlign,
    walletIntroText:
      gs.placeholders?.walletIntroText ?? defaultTemplate.walletIntroText,
    walletIntroFontSize:
      gs.placeholders?.walletIntroFontSize ??
      defaultTemplate.walletIntroFontSize,
    walletIntroStyles:
      gs.placeholders?.walletIntroStyles ?? defaultTemplate.walletIntroStyles,
    walletInstructionsText:
      gs.placeholders?.walletInstructionsText ??
      defaultTemplate.walletInstructionsText,
    walletInstructionsFontSize:
      gs.placeholders?.walletInstructionsFontSize ??
      defaultTemplate.walletInstructionsFontSize,
    walletInstructionsStyles:
      gs.placeholders?.walletInstructionsStyles ??
      defaultTemplate.walletInstructionsStyles,
    calendarProviders: {
      ...defaultTemplate.calendarProviders,
      ...(gs.placeholders?.calendarProviders || {}),
    },
    calendarProviderLogos: {
      ...(defaultTemplate.calendarProviderLogos || {}),
      ...(gs.placeholders?.calendarProviderLogos || {}),
    },
    calendarProviderLabels: {
      ...(defaultTemplate.calendarProviderLabels || {}),
      ...(gs.placeholders?.calendarProviderLabels || {}),
    },
    calendarProviderLogoHeight:
      gs.placeholders?.calendarProviderLogoHeight ??
      defaultTemplate.calendarProviderLogoHeight,
    calendarProviderButtonShape:
      gs.placeholders?.calendarProviderButtonShape ??
      defaultTemplate.calendarProviderButtonShape,
    includeCalendarButton:
      gs.placeholders?.includeCalendarButton ??
      defaultTemplate.includeCalendarButton,
    calendarButtonText:
      gs.placeholders?.calendarButtonText ??
      defaultTemplate.calendarButtonText,
    calendarButtonBackgroundColor:
      gs.placeholders?.calendarButtonBackgroundColor ??
      defaultTemplate.calendarButtonBackgroundColor,
    calendarButtonTextColor:
      gs.placeholders?.calendarButtonTextColor ??
      defaultTemplate.calendarButtonTextColor,
    calendarButtonFontSize:
      gs.placeholders?.calendarButtonFontSize ??
      defaultTemplate.calendarButtonFontSize,
    calendarButtonPaddingX:
      gs.placeholders?.calendarButtonPaddingX ??
      defaultTemplate.calendarButtonPaddingX,
    calendarButtonPaddingY:
      gs.placeholders?.calendarButtonPaddingY ??
      defaultTemplate.calendarButtonPaddingY,
    calendarButtonBorderRadius:
      gs.placeholders?.calendarButtonBorderRadius ??
      defaultTemplate.calendarButtonBorderRadius,
    calendarButtonFileName:
      gs.placeholders?.calendarButtonFileName ??
      defaultTemplate.calendarButtonFileName,
    calendarEventTitle:
      gs.placeholders?.calendarEventTitle ??
      defaultTemplate.calendarEventTitle,
    calendarEventLocation:
      gs.placeholders?.calendarEventLocation ??
      defaultTemplate.calendarEventLocation,
    calendarEventDescription:
      gs.placeholders?.calendarEventDescription ??
      defaultTemplate.calendarEventDescription,
    calendarEventStart:
      gs.placeholders?.calendarEventStart ??
      defaultTemplate.calendarEventStart,
    calendarEventEnd:
      gs.placeholders?.calendarEventEnd ??
      defaultTemplate.calendarEventEnd,
    calendarEventUrl:
      gs.placeholders?.calendarEventUrl ??
      defaultTemplate.calendarEventUrl,
    calendarEventUid:
      gs.placeholders?.calendarEventUid ??
      defaultTemplate.calendarEventUid,
    regmailEnabled:
      gs.placeholders?.regmailEnabled ?? defaultTemplate.regmailEnabled,
  };

  return merged;
}
